import { useGetSettlementHistoryApiV1Suspense } from "@/api/__generated__/api.schema.ts";
import {
  type Campaign,
  SettlementHistoryItemStatus,
  SettlementHistoryItemType,
  WorkspaceChannelPlatform,
} from "@/api/__generated__/models";
import { Profile } from "@/components/profile";
import { useWorkspaceId } from "@/hooks/auth/use-workspace";
import { useCampaignInfo } from "@/routes/campaigns/hooks/use-campaign-info";
import { formatCurrency } from "@/utils/format";
import {
  Badge,
  Icon,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadRow,
  TableHeader,
  TableRow,
  Thumbnail,
  ThumbnailImage,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  Typography,
  getAssetsUrl,
} from "@backpac/monica";
import { Temporal } from "temporal-polyfill";
import { match } from "ts-pattern";

export function SettlementHistoryTable() {
  const workspaceId = useWorkspaceId();
  const {
    data: {
      data: { items: settlementHistory },
    },
  } = useGetSettlementHistoryApiV1Suspense(workspaceId);

  return (
    <Table>
      <TableHeader>
        <TableHeadRow>
          <TableHead className="pl-6">Date</TableHead>
          <TableHead>Channel</TableHead>
          <TableHead>Platform</TableHead>
          <TableHead>Type</TableHead>
          <TableHead>Description</TableHead>
          <TableHead>Status</TableHead>
          <TableHead className="pr-6">Amount</TableHead>
        </TableHeadRow>
      </TableHeader>
      <TableBody>
        {settlementHistory.length === 0 ? (
          <SettlementHistoryTable.EmptyBody />
        ) : (
          settlementHistory.map((item) => (
            <TableRow key={item.id}>
              <TableCell className="pl-6 py-6">
                <Typography size="xs" color="textPrimary">
                  {Temporal.PlainDateTime.from(item.date).toLocaleString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  })}
                </Typography>
              </TableCell>
              <TableCell className="py-6">
                <Profile
                  name={item.channel?.name ?? "Unknown"}
                  profileImageUrl={item.channel?.thumbnailUrl}
                  description={item.channel?.handle}
                  size="sm"
                  className="rounded-full"
                />
              </TableCell>
              <TableCell>
                <SettlementHistoryTable.Platform platform={item.channel?.platform} />
              </TableCell>
              <TableCell>
                <SettlementHistoryTable.Type id={item.campaignId} type={item.type} />
              </TableCell>
              <TableCell>
                <SettlementHistoryTable.Description id={item.campaignId} date={item.settlementMonth} />
              </TableCell>
              <TableCell>
                <SettlementHistoryTable.StatusBadge status={item.status} />
              </TableCell>
              <TableCell className="pr-6 text-right">
                <Typography size="xs" color="textPrimary" weight={600}>
                  {formatCurrency(item.amount)}
                </Typography>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

SettlementHistoryTable.Platform = function PayoutPlatform({
  platform,
}: { platform: WorkspaceChannelPlatform | undefined }) {
  return match(platform)
    .with(WorkspaceChannelPlatform.YOUTUBE_SHORTS, () => (
      <Thumbnail className="size-6">
        <ThumbnailImage src={getAssetsUrl("sns/youtube.png")} />
      </Thumbnail>
    ))
    .with(WorkspaceChannelPlatform.TIKTOK, () => (
      <Thumbnail className="size-6">
        <ThumbnailImage src={getAssetsUrl("sns/tiktok.png ")} />
      </Thumbnail>
    ))
    .with(WorkspaceChannelPlatform.INSTAGRAM, () => (
      <Thumbnail className="size-6">
        <ThumbnailImage src={getAssetsUrl("sns/instagram.png")} />
      </Thumbnail>
    ))
    .with(undefined, () => null)
    .exhaustive();
};

SettlementHistoryTable.EmptyBody = function PayoutHistoryEmptyBody() {
  return (
    <TableRow className="hover:bg-transparent">
      <TableCell colSpan={7} className="text-center">
        <div className="space-y-3 py-10 text-white">
          <Icon name="invoice" bgColor="brandSkyblue" iconSize="24px" />
          <div className="space-y-2">
            <Typography size="md" color="textPrimary" weight={600}>
              No earnings yet
            </Typography>
            <Typography size="xs" color="textMuted">
              Once you start earning, the details of your balance will appear here.
            </Typography>
          </div>
        </div>
      </TableCell>
    </TableRow>
  );
};

SettlementHistoryTable.Type = function PayoutType({
  type,
  id,
}: { type: SettlementHistoryItemType; id: string | null }) {
  return match(type)
    .with(SettlementHistoryItemType.BGM, () => "BackPac's BGM")
    .with(SettlementHistoryItemType.FACTORING_PAYOUT, () => "Factoring Payout")
    .with(SettlementHistoryItemType.CAMPAIGN, () =>
      id != null ? <CampaignInfo campaignId={id} pick="title" /> : "Unknown",
    )
    .exhaustive();
};

function CampaignInfo({ campaignId, pick }: { campaignId: string; pick: keyof Campaign }) {
  const info = useCampaignInfo(campaignId);
  return <>{info[pick]}</>;
}

SettlementHistoryTable.Description = function PayoutDescription({ id, date }: { id: string | null; date: string }) {
  if (id == null) {
    return `Earnings for ${formatDateToRangeOfMonth(date)}`;
  }
  return <CampaignInfo campaignId={id} pick="description" />;
};

SettlementHistoryTable.StatusBadge = function PayoutStatusBadge({ status }: { status: SettlementHistoryItemStatus }) {
  return match(status)
    .with(SettlementHistoryItemStatus.AVAILABLE, () => (
      <Tooltip>
        <TooltipTrigger>
          <Badge theme="grey">Available</Badge>
        </TooltipTrigger>
        <TooltipContent>Your funds have been added to your balance and are ready for withdrawal.</TooltipContent>
      </Tooltip>
    ))
    .with(SettlementHistoryItemStatus.IN_PROGRESS, () => (
      <Tooltip>
        <TooltipTrigger>
          <Badge theme="orange" className="whitespace-pre">
            In progress
          </Badge>
        </TooltipTrigger>
        <TooltipContent>Your withdrawal request has been received and is currently being processed.</TooltipContent>
      </Tooltip>
    ))
    .with(SettlementHistoryItemStatus.PAID, () => <Badge theme="green">Paid</Badge>)
    .exhaustive();
};

function formatDateToRangeOfMonth(dateString: string): string {
  if (!dateString.match(/^\d{4}-\d{2}$/)) {
    return dateString;
  }

  const [year, month] = dateString.split("-").map(Number);
  const monthName = new Date(year, month - 1, 1).toLocaleString("en-US", { month: "long" });

  // Get the last day of the month
  const lastDay = new Date(year, month, 0).getDate();

  return `${monthName} 1-${lastDay}`;
}
